<!--
	Field — labeled input with a mono-caps label and inline validation. The low-level
	text primitive EmailCapture and any future form composes.
-->
<script lang="ts">
	import type { HTMLInputAttributes } from 'svelte/elements';

	let {
		label,
		name,
		type = 'text',
		value = $bindable(''),
		placeholder,
		error,
		required = false,
		autocomplete,
		id = `field-${name}`,
		class: klass = ''
	}: {
		label: string;
		name: string;
		type?: HTMLInputAttributes['type'];
		value?: string;
		placeholder?: string;
		error?: string;
		required?: boolean;
		autocomplete?: HTMLInputAttributes['autocomplete'];
		id?: string;
		class?: string;
	} = $props();
</script>

<div class="field {klass}">
	<label class="field__label" for={id}>{label}</label>
	<input
		{id}
		{name}
		{type}
		{placeholder}
		{required}
		{autocomplete}
		bind:value
		class="field__input"
		aria-invalid={error ? 'true' : undefined}
		aria-describedby={error ? `${id}-err` : undefined}
	/>
	{#if error}
		<p id="{id}-err" class="field__error">{error}</p>
	{/if}
</div>

<style>
	.field {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
	.field__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.field__input {
		height: 2.75rem;
		padding-inline: 0.875rem;
		border-radius: var(--radius-md);
		background-color: var(--color-surface);
		border: 1px solid var(--color-line-strong);
		color: var(--color-ink-900);
		font-family: var(--font-sans);
		font-size: var(--text-body);
		transition:
			border-color var(--dur-fast) var(--ease-out),
			box-shadow var(--dur-fast) var(--ease-out);
	}
	.field__input::placeholder {
		color: var(--color-ink-300);
	}
	.field__input:hover {
		border-color: var(--color-ink-300);
	}
	.field__input:focus-visible {
		outline: none;
		border-color: var(--color-cut-600);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-cut-400) 35%, transparent);
	}
	.field__input[aria-invalid='true'] {
		border-color: var(--color-signal-red);
	}
	.field__input[aria-invalid='true']:focus-visible {
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-signal-red) 22%, transparent);
	}
	.field__error {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-signal-red);
	}

	/* On a dark surface the field flips to the app's own input. */
	:global(.sec--dark) .field__input,
	:global(.app-surface) .field__input {
		background-color: var(--color-app-800);
		border-color: var(--color-app-700);
		color: var(--color-bone-100);
	}
	:global(.sec--dark) .field__input::placeholder,
	:global(.app-surface) .field__input::placeholder {
		color: var(--color-app-500);
	}
	:global(.sec--dark) .field__label,
	:global(.app-surface) .field__label {
		color: var(--color-bone-500);
	}
</style>
